import React from "react";
import { formatClock, formatDayTime } from "../../api/shape.js";
import { scheduleOptionLabel } from "../../api/reportSchedule.js";

/**
 * The contents of the two confirm dialogs of the approve -> PDF -> publish
 * flow (design Approve.dc.html panels 1 and 4). Shared by the real modal
 * (Report.jsx, inside <Dialog>) and by the design gallery fixture, so the
 * artboard gate compares the very markup the site manager sees.
 */

/**
 * SPEC-CIS-REPORT-SCHEDULED-PUBLISH-001 section 6: what happens after the
 * approval. Shown only when the server offers a schedule (`d.scheduleDefault`);
 * Schedule is the default.
 */
function AfterApprove({ d, onChoose }) {
  const label = scheduleOptionLabel(d.scheduleDefault);
  const next = d.next || "schedule";
  return (
    <fieldset className="wr-box" data-testid="report-dialog-approve-next" style={{ margin: 0 }}>
      <legend style={{ fontWeight: 600 }}>Then</legend>
      <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input type="radio" name="wr-approve-next" value="schedule" data-testid="report-dialog-approve-schedule" checked={next === "schedule"} onChange={() => onChoose("schedule")} />
        {label}
      </label>
      <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input type="radio" name="wr-approve-next" value="publish" data-testid="report-dialog-approve-publish-now" checked={next === "publish"} onChange={() => onChoose("publish")} />
        Publish now
      </label>
    </fieldset>
  );
}

export function ApproveBody({ d, onCancel, onConfirm, onChoose = () => {}, titleId = "wr-dlg-title" }) {
  const versionLabel = d.version ? ` v${d.version}` : "";
  const offersSchedule = Boolean(d.scheduleDefault && d.scheduleDefault.publishAtUtc);
  const next = offersSchedule ? d.next || "schedule" : null;
  const when = d.editedAt
    ? `last edited in Word ${formatClock(d.editedAt)}`
    : d.generatedAt
    ? `generated ${formatClock(d.generatedAt)}`
    : "not edited since it was generated";
  return (
    <>
      <h2 id={titleId}>Approve the draft{versionLabel}?</h2>
      <p>
        Period {d.periodText} · {when}. Approving locks this version and makes a PDF of exactly what you see in Word.
      </p>
      <ul className="wr-box" aria-label="Checks" style={{ listStyle: "none", margin: 0 }}>
        <li>{d.confirmedAt ? `✓ Data confirmed ${formatDayTime(d.confirmedAt)}` : "✓ Data confirmed"}</li>
        <li>• Checked now: no unresolved review markers</li>
        <li>• Locks this version until you reopen it</li>
      </ul>
      {offersSchedule ? <AfterApprove d={d} onChoose={onChoose} /> : null}
      <div className="wr-dlgbtns">
        <button type="button" className="wr-btn" data-autofocus="true" onClick={onCancel}>Cancel</button>
        <button type="button" className="wr-btn pri" data-testid="report-dialog-approve-confirm" onClick={onConfirm}>
          Approve{versionLabel}{next === "schedule" ? " and schedule" : next === "publish" ? " and publish" : ""}
        </button>
      </div>
    </>
  );
}

export function PublishBody({ d, onCancel, onConfirm, titleId = "wr-dlg-title" }) {
  return (
    <>
      <h2 id={titleId}>Publish the approved report to the client?</h2>
      <dl className="wr-kv">
        <dt>Where</dt>
        <dd>{d.code} project site · Weekly Reports</dd>
        <dt>File</dt>
        <dd title={d.fileName}>{d.fileName || "Approved report (PDF)"}</dd>
        <dt>Who sees it</dt>
        <dd>{d.whoSees || `Members of the ${d.code} project site`}</dd>
      </dl>
      <p>The client gets the PDF only. The Word file stays internal.</p>
      <div className="wr-dlgbtns">
        <button type="button" className="wr-btn" data-autofocus="true" onClick={onCancel}>Cancel</button>
        <button type="button" className="wr-btn pri" data-testid="report-dialog-publish-confirm" onClick={onConfirm}>Publish PDF</button>
      </div>
    </>
  );
}
